import { useQuery } from '@tanstack/react-query';
import { graphQlClient } from '@config/graphqlClient';
import TasksGraphQL from '@graphql/task.queries';
import { useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { UserRoles } from '@models/user.model';
import { PendingApproval } from '@models/task.model';

export const PENDING_APPROVALS_QUERY_KEY = 'pending-approvals';

const OFFICE_ROLES: string[] = [
  UserRoles.OWNER,
  UserRoles.ADMIN,
  UserRoles.PROJECT_MANAGER,
  UserRoles.SUPERINTENDENT,
];

/**
 * Sprint 12, story-054/055 (U11/N2). The viewer-scoped approvals inbox — mirrors
 * `usePendingBudgetRequests`'s exact shape (`staleTime: 0`, `refetchOnMount: 'always'`, no
 * `refetchInterval` — real-time updates come from `useOrganizationEventsSubscription`'s new
 * `TASK_APPROVAL` entry). Client-side role gate mirrors `BudgetNotification`'s own gating,
 * backstopped server-side by `pendingApprovals`'s own fast-reject for non-office roles.
 */
export const usePendingApprovals = (organizationId?: string | null) => {
  const userRole = useAppSelector((state: RootState) => state.user.role);
  const isOfficeRole = !!userRole && OFFICE_ROLES.includes(userRole);

  return useQuery({
    queryKey: [PENDING_APPROVALS_QUERY_KEY, organizationId],
    queryFn: async () => {
      const data: any = await graphQlClient.request(TasksGraphQL.queries.pendingApprovals, {
        organizationId,
      });
      return (data.pendingApprovals || []) as PendingApproval[];
    },
    enabled: !!organizationId && isOfficeRole,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

export default usePendingApprovals;
